import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Loader2, FileText, HardHat, Lock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";

const STATUSES = ["new", "reviewing", "interview", "hired", "rejected"];
const COLORS = {
  new: "bg-primary/10 text-primary", reviewing: "bg-blue-100 text-blue-700", interview: "bg-purple-100 text-purple-700",
  hired: "bg-green-100 text-green-700", rejected: "bg-muted text-muted-foreground",
};
const JOB_LABELS = { "cdl-driver": "CDL Driver", "concrete-laborer": "Concrete Laborer" };

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [jobFilter, setJobFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");
  const token = localStorage.getItem("cg_token");

  const load = () => {
    setLoading(true);
    const params = [];
    if (jobFilter !== "all") params.push(`job=${jobFilter}`);
    if (statusFilter !== "all") params.push(`status=${statusFilter}`);
    api.get(`/applications${params.length ? `?${params.join("&")}` : ""}`)
      .then((r) => setApps(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [jobFilter, statusFilter]);

  const changeStatus = async (id, application_status) => {
    try {
      await api.patch(`/applications/${id}`, { application_status });
      toast.success(`Marked as ${application_status}`);
      setApps((a) => a.map((x) => (x.id === id ? { ...x, application_status } : x)));
      if (active?.id === id) setActive({ ...active, application_status });
    } catch { toast.error("Update failed"); }
  };

  const saveNotes = async () => {
    try {
      await api.patch(`/applications/${active.id}`, { admin_notes: notes });
      toast.success("Notes saved");
      setActive({ ...active, admin_notes: notes });
    } catch { toast.error("Save failed"); }
  };

  const openApp = (a) => { setActive(a); setNotes(a.admin_notes || ""); };
  const authUrl = (id) => `${fileUrl(id)}?auth=${token}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Applications</h1>
          <p className="text-muted-foreground">Job applications submitted through the careers pages.</p>
        </div>
        <div className="flex gap-3">
          <Select value={jobFilter} onValueChange={setJobFilter}>
            <SelectTrigger className="w-44 rounded-sm" data-testid="app-job-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="cdl-driver">CDL Driver</SelectItem>
              <SelectItem value="concrete-laborer">Concrete Laborer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="app-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : apps.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center text-muted-foreground">No applications yet.</div>
      ) : (
        <div className="bg-card border border-border overflow-hidden">
          <div className="divide-y divide-border">
            {apps.map((a) => (
              <div key={a.id} className="p-5 hover:bg-muted/40 transition-colors" data-testid={`app-row-${a.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <button onClick={() => openApp(a)} className="flex-1 text-left min-w-0" data-testid={`app-open-${a.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{a.first_name} {a.last_name}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 ${COLORS[a.application_status]}`}>{a.application_status.toUpperCase()}</span>
                      <span className="text-xs font-semibold bg-secondary/5 text-secondary flex items-center gap-1"><HardHat className="h-3 w-3 text-primary" /> {JOB_LABELS[a.job_slug] || a.job_title}</span>
                      {(a.resume_id || a.document_ids?.length > 0) && <span className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="h-3 w-3" /> {(a.resume_id ? 1 : 0) + (a.document_ids?.length || 0)}</span>}
                    </div>
                    <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {a.phone}</span>
                      <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {a.email}</span>
                      {a.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.city}, {a.state}</span>}
                      {a.experience_years && <span>Exp: {a.experience_years}</span>}
                    </div>
                  </button>
                  <div className="lg:w-44 shrink-0">
                    <Select value={a.application_status} onValueChange={(v) => changeStatus(a.id, v)}>
                      <SelectTrigger className="rounded-sm h-9" data-testid={`app-status-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{active.first_name} {active.last_name}</DialogTitle>
                <p className="text-sm text-muted-foreground">Applying for <strong>{JOB_LABELS[active.job_slug] || active.job_title}</strong></p>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> <a href={`tel:${active.phone}`} className="hover:text-primary">{active.phone}</a></div>
                  <div className="flex items-center gap-2 truncate"><Mail className="h-4 w-4 text-primary" /> <a href={`mailto:${active.email}`} className="hover:text-primary truncate">{active.email}</a></div>
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {[active.city, active.state].filter(Boolean).join(", ") || "—"}</div>
                </div>

                <div className="bg-muted p-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {[
                    ["Availability", active.availability], ["Earliest start", active.start_date],
                    ["Construction exp.", active.experience_years], ["Previous employer", active.previous_employer],
                    ["Driver's license", active.has_license], ["Reliable transport", active.reliable_transport],
                    ["Work authorized", active.work_authorized],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}><span className="text-xs font-bold text-muted-foreground uppercase block">{k}</span>{v}</div>
                  ))}
                </div>

                {active.answers && Object.keys(active.answers).length > 0 && (
                  <div>
                    <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Screening Answers</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm bg-primary/5 border border-primary/20 p-4">
                      {Object.entries(active.answers).filter(([, v]) => v && (!Array.isArray(v) || v.length)).map(([k, v]) => (
                        <div key={k}><span className="text-xs font-bold text-muted-foreground uppercase block">{k}</span>{Array.isArray(v) ? v.join(", ") : v}</div>
                      ))}
                    </div>
                  </div>
                )}

                {(active.resume_id || active.document_ids?.length > 0) && (
                  <div>
                    <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1"><Lock className="h-3 w-3 text-primary" /> Secure Documents</div>
                    <div className="flex flex-wrap gap-2">
                      {active.resume_id && (
                        <a href={authUrl(active.resume_id)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm font-semibold bg-secondary text-white px-3 py-2 rounded-sm hover:bg-secondary/90" data-testid="app-resume-link">
                          <FileText className="h-4 w-4" /> Résumé
                        </a>
                      )}
                      {(active.document_ids || []).map((id, i) => (
                        <a key={id} href={authUrl(id)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm font-semibold bg-card border border-border px-3 py-2 rounded-sm hover:border-primary" data-testid={`app-doc-link-${i}`}>
                          <FileText className="h-4 w-4 text-primary" /> Document {i + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">Internal Notes</div>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-sm" data-testid="app-notes" />
                  <Button onClick={saveNotes} size="sm" className="mt-2 bg-secondary rounded-sm font-bold" data-testid="app-save-notes">Save Notes</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
